import React, { FC, useMemo } from "react";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { useTranslation } from "react-i18next";

import CustomButton from "ui/Button";
import CustomDropDownList from "ui/dropDown";
import { ICON_NAMES } from "utils/constant";
import { getPackagesApi, getProductsApi } from "api/common";
import { LandingNoteLine } from "interface/landingNote";

interface Form extends LandingNoteLine {
	id: number;
	inEdit: boolean;
	onfocus?: boolean;
	focusedField?: string;
}

interface StepTwoMobileListProps {
	form: Form[];
	onUpdateLine: (line: Form) => void;
	onSelectPackage: (line: Form) => void;
	onOpenNote: (line: Form) => void;
	onDelete: (line: Form) => void;
	hasMeetRequirement: (data: Form, field: string) => boolean;
	showRequirementMetError: boolean;
}

const numericFields: Array<{
	field:
		| "numberOfUnits"
		| "numberOfPallets"
		| "numberOfPieces"
		| "averageLiveWeightKg"
		| "grossLiveWeightKg"
		| "netLiveWeightKg";
	labelKey: string;
	decimals: number;
	required: boolean;
}> = [
	{ field: "numberOfUnits", labelKey: "Units", decimals: 0, required: false },
	{ field: "numberOfPallets", labelKey: "Pallets", decimals: 0, required: false },
	{ field: "numberOfPieces", labelKey: "Pieces", decimals: 0, required: true },
	{ field: "averageLiveWeightKg", labelKey: "Average", decimals: 2, required: false },
	{ field: "grossLiveWeightKg", labelKey: "GrossWeight", decimals: 2, required: true },
	{ field: "netLiveWeightKg", labelKey: "NetWeight", decimals: 2, required: true },
];

const StepTwoMobileList: FC<StepTwoMobileListProps> = ({
	form,
	onUpdateLine,
	onSelectPackage,
	onOpenNote,
	onDelete,
	hasMeetRequirement,
	showRequirementMetError,
}) => {
	const { t } = useTranslation();

	const totals = useMemo(() => {
		const sum = (field: keyof Form) =>
			form.reduce((acc, item) => acc + (Number(item[field] || 0) || 0), 0);

		return {
			units: sum("numberOfUnits"),
			pallets: sum("numberOfPallets"),
			pieces: sum("numberOfPieces"),
			average: sum("averageLiveWeightKg"),
			gross: sum("grossLiveWeightKg"),
			net: sum("netLiveWeightKg"),
		};
	}, [form]);

	const updateField = (
		line: Form,
		field: keyof Form,
		value: number | string | null | undefined,
	) => {
		const updated = {
			...line,
			[field]: value,
		} as Form;

		if (
			field === "grossLiveWeightKg" &&
			(!updated.netLiveWeightKg || updated.netLiveWeightKg === 0)
		) {
			updated.netLiveWeightKg = value as number;
		}

		onUpdateLine(updated);
	};

	return (
		<div className="landing-note-mobile-lines">
			{form.length === 0 && (
				<div className="landing-note-mobile-empty">{t("AtLeastOneLineRequired")}</div>
			)}

			{form.map((line) => {
				return (
					<div key={line.id} className="landing-note-line-card">
						<div className="landing-note-line-editor">
								<div className="landing-note-line-field">
									<label>{t("Product")}</label>
									<CustomDropDownList
										data={[]}
										value={
											line.productId
												? { id: line.productId, name: line.product }
												: ({} as any)
										}
										onSelect={(data) => {
											const updated = {
												...line,
												productId: data.id,
												product: data.name,
												piecesMandatory:
													data.isNumberOfUnitsMandatory === true ? 1 : 0,
											} as Form;
											onUpdateLine(updated);
										}}
										serverFilterApi={getProductsApi}
										defaultItem={{ id: null, name: t("ChooseProduct") }}
										filterable={true}
										serverFilter={true}
										textField="name"
										dataItemKey="id"
										showError={
											!hasMeetRequirement(line, "productId") &&
											showRequirementMetError
										}
					/>
								</div>

								<div className="landing-note-line-field">
									<label>{t("Packaging")}</label>
									<CustomDropDownList
										data={[]}
										value={
											line.packagingCode
												? { code: line.packagingCode, name: line.packaging }
												: ({} as any)
										}
										onSelect={(data) => {
											const updated = {
												...line,
												packagingCode: data.code,
												packaging: data.name,
											} as Form;
											onSelectPackage(updated);
										}}
										serverFilterApi={getPackagesApi}
										defaultItem={{ code: null, name: t("ChoosePackaging") }}
										filterable={true}
										serverFilter={true}
										textField="name"
										dataItemKey="code"
										showError={
											!hasMeetRequirement(line, "packagingCode") &&
											showRequirementMetError
										}
					/>
								</div>

								<div className="landing-note-line-grid">
									{numericFields.map((fieldItem) => (
										<div className="landing-note-line-field" key={fieldItem.field}>
											<label>{t(fieldItem.labelKey)}</label>
											<NumericTextBox
												value={
													(line[fieldItem.field] as number | null | undefined) ?? null
												}
												format={fieldItem.decimals === 0 ? "n0" : "n2"}
												spinners={false}
												onChange={(e) => {
													const value = e.value == null ? null : Number(e.value);
													updateField(line, fieldItem.field, value);
												}}
												style={{
													width: "100%",
													borderColor:
													(fieldItem.required &&
													!hasMeetRequirement(line, fieldItem.field) &&
														showRequirementMetError)
															? "red"
															: undefined,
												}}
											/>
										</div>
									))}
								</div>

								<div className="landing-note-line-card-actions">
									<CustomButton
										onClick={() => onOpenNote(line)}
										label={"Notat"}
										iconName={ICON_NAMES.Note}
										btnStyle={{ width: "100%", minHeight: "44px" }}
									/>
									<CustomButton
										onClick={() => onDelete(line)}
										label={t("DeleteLine")}
										iconName={ICON_NAMES.Delete}
										btnStyle={{ width: "100%", minHeight: "44px" }}
									/>
								</div>
							</div>
					</div>
				);
			})}

			{form.length > 0 && (
				<div className="landing-note-mobile-totals">
					<div>{t("Units")}: {totals.units.toFixed(0)}</div>
					<div>{t("Pallets")}: {totals.pallets.toFixed(0)}</div>
					<div>{t("Pieces")}: {totals.pieces.toFixed(0)}</div>
					<div>{t("Average")}: {totals.average.toFixed(2)}</div>
					<div>{t("GrossWeight")}: {totals.gross.toFixed(2)}</div>
					<div>{t("NetWeight")}: {totals.net.toFixed(2)}</div>
				</div>
			)}
		</div>
	);
};

export default StepTwoMobileList;
